// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React from 'react';
import { Box, useTheme } from '@mui/system';
import { Dialog, IconButton, Typography, CircularProgress, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import kmlUploaded from 'Assets/kml-location-active.png';
import nearmapLogo from 'Assets/nearmap-logo-icon.png';
import eagleviewLogo from 'Assets/eagleview-logo.png';
import stylesDef from './styles';

interface CheckImageryCoverageDialogProps {
  open: boolean;
  onClose: () => void;
  loading: boolean;
  hasCoverage: boolean | null;
  onProceed: () => void;
}

export const CheckImageryCoverageDialog = ({
  open,
  onClose,
  loading,
  hasCoverage,
  onProceed,
}: CheckImageryCoverageDialogProps) => {
  const theme = useTheme();
  const styles = stylesDef(theme);

  return (
    <Dialog open={open} onClose={onClose} sx={styles.dialog}>
      <IconButton sx={styles.closeButton} onClick={onClose}>
        <CloseIcon />
      </IconButton>

      <Box sx={styles.headerIcon}>
        <img src={kmlUploaded} alt="" style={styles.headerImage} />
      </Box>

      <Typography sx={styles.title}>Check Imagery Coverage</Typography>

      {!loading && hasCoverage === true && (
        <Box sx={styles.successBanner}>
          <CheckCircleIcon sx={styles.successIcon} />
          <Typography sx={styles.successBannerText}>
            We have full coverage! Proceed to processing your order.
          </Typography>
        </Box>
      )}

      {!loading && hasCoverage === false && (
        <Box sx={styles.errorBanner}>
          <WarningAmberIcon sx={styles.warningIcon} />
          <Typography sx={styles.errorBannerText}>
            We don't have full coverage but we are checking alternative data sources
            from our collection of partners. We will get back to you in
            {' '}
            <b>1 business day</b>
            .
          </Typography>
        </Box>
      )}

      <Box sx={styles.providerRow}>
        <img style={styles.providerLogo} src={nearmapLogo} alt="Nearmap" />
        <Typography sx={styles.providerName}>Nearmap</Typography>
        {loading && (
          <Box sx={styles.checkingStatus}>
            <CircularProgress size={16} />
            <Typography>Checking...</Typography>
          </Box>
        )}
      </Box>

      <Box sx={styles.providerRow}>
        <img style={styles.providerLogo} src={eagleviewLogo} alt="Eagleview" />
        <Typography sx={styles.providerName}>Eagleview</Typography>
        {loading && (
          <Box sx={styles.checkingStatus}>
            <CircularProgress size={16} />
            <Typography>Checking...</Typography>
          </Box>
        )}
      </Box>

      {!loading && hasCoverage === true && (
        <Button sx={styles.proceedButton} onClick={onProceed}>
          Proceed
        </Button>
      )}
    </Dialog>
  );
};

export default CheckImageryCoverageDialog;
